import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { UserPlus, MessageCircle, TrendingUp, X, Check, Clock } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShareProgressDialog } from "./ShareProgressDialog";

interface Partnership {
  id: string;
  partner_id: string;
  status: string;
  partnership_type: string;
  shared_goals: string[];
  check_in_frequency: string;
  created_at: string;
  accepted_at: string | null;
  profiles?: {
    display_name: string;
    avatar_url: string | null;
  };
}

export function MyPartners() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [partnerEmail, setPartnerEmail] = useState("");
  const [partnershipType, setPartnershipType] = useState("general");
  const [checkInFrequency, setCheckInFrequency] = useState("daily");

  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: partnerships, isLoading } = useQuery({
    queryKey: ['partnerships', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('accountability_partnerships')
        .select('*')
        .or(`user_id.eq.${user.id},partner_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch partner profiles separately
      if (!data || data.length === 0) return [];
      
      const partnerIds = data.map(p => 
        p.user_id === user.id ? p.partner_id : p.user_id
      );
      
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .in('user_id', partnerIds);
      
      const profileMap = new Map(profiles?.map(p => [p.user_id, p]));
      
      return data.map(p => ({
        ...p,
        profiles: profileMap.get(p.user_id === user.id ? p.partner_id : p.user_id),
      })) as unknown as Partnership[];
    },
    enabled: !!user?.id,
  });

  const createPartnershipMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      if (!partnerEmail) throw new Error("Email required");

      // Search for user by email using edge function
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        'https://jbdsdzmyqkfbqijnoqmo.supabase.co/functions/v1/search-user-by-email',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ email: partnerEmail }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to find user');
      }

      const foundUser = await response.json();

      // Check if partnership already exists
      const { data: existing } = await supabase
        .from('accountability_partnerships')
        .select('id, status')
        .or(`user_id.eq.${user.id},partner_id.eq.${user.id}`)
        .or(`user_id.eq.${foundUser.user_id},partner_id.eq.${foundUser.user_id}`)
        .single();

      if (existing) {
        if (existing.status === 'active') {
          throw new Error('Partnership already exists');
        } else if (existing.status === 'pending') {
          throw new Error('Partnership request already sent');
        }
      }

      // Create partnership request
      const { error } = await supabase
        .from('accountability_partnerships')
        .insert({
          user_id: user.id,
          partner_id: foundUser.user_id,
          partnership_type: partnershipType,
          check_in_frequency: checkInFrequency,
          status: 'pending',
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partnerships'] });
      toast.success("Partnership request sent!");
      setOpen(false);
      setPartnerEmail("");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to send request");
    },
  });

  const acceptPartnershipMutation = useMutation({
    mutationFn: async (partnershipId: string) => {
      const { error } = await supabase
        .from('accountability_partnerships')
        .update({
          status: 'active',
          accepted_at: new Date().toISOString(),
        })
        .eq('id', partnershipId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partnerships'] });
      toast.success("Partnership accepted!");
    },
  });

  const endPartnershipMutation = useMutation({
    mutationFn: async (partnershipId: string) => {
      const { error } = await supabase
        .from('accountability_partnerships')
        .update({
          status: 'ended',
          ended_at: new Date().toISOString(),
        })
        .eq('id', partnershipId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partnerships'] });
      toast.success("Partnership ended");
    },
  });

  const activePartnerships = partnerships?.filter(p => p.status === 'active') || [];
  const pendingPartnerships = partnerships?.filter(p => p.status === 'pending') || [];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading partners...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Find an Accountability Partner</CardTitle>
            <div className="flex gap-2">
              <ShareProgressDialog />
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2">
                    <UserPlus className="h-4 w-4" />
                    Add Partner
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite Accountability Partner</DialogTitle>
                  <DialogDescription>
                    Enter the email of someone already on Therappi® to send them a partnership request
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Partner's Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="partner@example.com"
                      value={partnerEmail}
                      onChange={(e) => setPartnerEmail(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="type">Partnership Type</Label>
                    <Select value={partnershipType} onValueChange={setPartnershipType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General Support</SelectItem>
                        <SelectItem value="sleep">Sleep Goals</SelectItem>
                        <SelectItem value="medication">Medication Adherence</SelectItem>
                        <SelectItem value="nutrition">Nutrition</SelectItem>
                        <SelectItem value="goals">Life Goals</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="frequency">Check-in Frequency</Label>
                    <Select value={checkInFrequency} onValueChange={setCheckInFrequency}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="biweekly">Bi-weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button
                      onClick={() => createPartnershipMutation.mutate()}
                      disabled={!partnerEmail || createPartnershipMutation.isPending}
                      className="flex-1"
                    >
                      Send Request
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardHeader>
      </Card>

      {pendingPartnerships.length > 0 && (
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pending Requests
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingPartnerships.map((partnership) => (
              <div key={partnership.id} className="flex items-center justify-between p-3 rounded-lg border bg-background">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {partnership.profiles?.display_name?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{partnership.profiles?.display_name || 'Unknown'}</p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {partnership.partnership_type} • {partnership.check_in_frequency} check-ins
                    </p>
                  </div>
                </div>
                {partnership.partner_id === user?.id && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => acceptPartnershipMutation.mutate(partnership.id)}
                      className="gap-2"
                    >
                      <Check className="h-4 w-4" />
                      Accept
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => endPartnershipMutation.mutate(partnership.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {activePartnerships.length === 0 && pendingPartnerships.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center space-y-3">
              <UserPlus className="h-12 w-12 mx-auto text-muted-foreground/50" />
              <p className="text-sm text-muted-foreground">
                No accountability partners yet. Invite someone to join you on your journey!
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {activePartnerships.length > 0 && (
        <div className="grid gap-4">
          {activePartnerships.map((partnership) => (
            <Card key={partnership.id}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="text-lg">
                        {partnership.profiles?.display_name?.[0]?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{partnership.profiles?.display_name || 'Unknown'}</h3>
                        <Badge variant="secondary" className="capitalize">
                          {partnership.partnership_type}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground capitalize mb-3">
                        {partnership.check_in_frequency} check-ins • Active since {new Date(partnership.accepted_at || partnership.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                      </p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="gap-2">
                          <MessageCircle className="h-4 w-4" />
                          Message
                        </Button>
                        <Button size="sm" variant="outline" className="gap-2">
                          <TrendingUp className="h-4 w-4" />
                          View Progress
                        </Button>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => endPartnershipMutation.mutate(partnership.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
